import React, { useEffect, useState } from "react";
import { PencilSimple, Plus, ShieldCheck } from "@phosphor-icons/react";

import ActaConfirmDialog from "@/components/ActaConfirmDialog";

import { acquisition as acquisitionApi } from "@/api";
import ActaStandardPage from "@/components/layout/ActaStandardPage";

export default function AcquisitionSettings() {
  const [sources, setSources] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [retention, setRetention] = useState(null);
  const [retentionConfirmOpen, setRetentionConfirmOpen] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    try {
      const [rows, retentionStatus] = await Promise.all([
        acquisitionApi.listSources(),
        acquisitionApi.retentionStatus(false),
      ]);
      setSources(Array.isArray(rows) ? rows : []);
      setRetention(retentionStatus || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de charger les sources.");
    }
  }

  useEffect(() => { load(); }, []);

  async function addSource(event) {
    event.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError("");
    try {
      await acquisitionApi.saveSource({
        ...(editingSource?.id ? { id: editingSource.id } : {}),
        name: name.trim(),
        type: editingSource?.type || "manual",
        active: editingSource?.active !== false,
      });
      setName("");
      setEditingSource(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de créer la source.");
    } finally {
      setSaving(false);
    }
  }

  function editSource(source) {
    setEditingSource(source);
    setName(source.name || "");
  }

  async function applyRetention() {
    setSaving(true);
    setError("");
    try {
      const result = await acquisitionApi.retentionStatus(true);
      setRetention(result || null);
      setRetentionConfirmOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible d'appliquer la conservation.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ActaStandardPage testId="acquisition-settings">
      {error ? <div className="mt-4 rounded-2xl border border-[var(--do-danger-border)] bg-[var(--do-danger-soft)] p-4 text-sm text-[var(--do-danger)]">{error}</div> : null}

      <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_360px]">
        <section className="lirah-card p-5">
          <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">Sources</h2>
          <div className="mt-4 divide-y divide-[var(--do-border)]">
            {sources.map((source) => (
              <div key={source.id} className="flex items-center justify-between gap-4 py-3">
                <div>
                  <div className="font-bold text-[var(--do-ink)]">{source.name}</div>
                  <div className="text-xs text-[var(--do-muted)]">{source.type === "integrated" ? "Intégrée" : "Manuelle"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="acta-status-badge">{source.active === false ? "Inactive" : "Active"}</span>
                  <button type="button" className="btn-secondary flex min-h-[38px] items-center gap-1 px-3 text-xs" onClick={() => editSource(source)}>
                    <PencilSimple size={15} /> Modifier
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        <form className="lirah-card p-5" onSubmit={addSource}>
          <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">{editingSource ? "Modifier la source" : "Nouvelle source"}</h2>
          <input className="mt-4 w-full rounded-xl border border-[var(--do-border)] bg-transparent px-3 py-3" value={name} onChange={(event) => setName(event.target.value)} placeholder="Ex. Salon Paris 2026" />
          <button className="btn-primary mt-4 flex w-full items-center justify-center gap-2 py-3" disabled={saving || !name.trim()}>
            <Plus size={18} /> {saving ? "Enregistrement…" : editingSource ? "Enregistrer" : "Ajouter"}
          </button>
        </form>
      </div>

      <section className="lirah-card mt-6 p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <ShieldCheck size={20} className="text-[var(--do-accent)]" />
              <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">Conservation des données</h2>
            </div>
            <p className="mt-2 text-sm text-[var(--do-muted)]">
              Les leads arrivés à échéance sans Contact Commerce peuvent être anonymisés. Les leads liés à Commerce nécessitent d’abord une coordination avec la fiche Contact.
            </p>
          </div>
          <button type="button" className="btn-secondary px-4 py-3" onClick={() => setRetentionConfirmOpen(true)} disabled={saving || !retention?.expired}>
            Appliquer la conservation
          </button>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <div className="rounded-xl border border-[var(--do-border)] p-3"><div className="text-xs text-[var(--do-muted)]">Échus</div><div className="mt-1 text-xl font-black">{retention?.expired || 0}</div></div>
          <div className="rounded-xl border border-[var(--do-border)] p-3"><div className="text-xs text-[var(--do-muted)]">Anonymisables</div><div className="mt-1 text-xl font-black">{retention?.unlinked || 0}</div></div>
          <div className="rounded-xl border border-[var(--do-border)] p-3"><div className="text-xs text-[var(--do-muted)]">À coordonner avec Commerce</div><div className="mt-1 text-xl font-black">{retention?.linkedRequiresCommerceCoordination || 0}</div></div>
        </div>
      </section>

      <ActaConfirmDialog
        open={retentionConfirmOpen}
        title="Appliquer la politique de conservation"
        description="Les leads échus non liés à Commerce seront anonymisés de manière irréversible. Les leads liés seront uniquement signalés pour coordination."
        confirmLabel="Appliquer"
        saving={saving}
        danger
        onClose={() => setRetentionConfirmOpen(false)}
        onConfirm={applyRetention}
      />
    </ActaStandardPage>
  );
}
